import { updateNotes } from "@/services/api/agents/notes";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  ColorPaletteProp,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/joy";
import { useQueryClient } from "@tanstack/react-query";
import useAgentNotes from "../useAgentNotes";
import { getFilteredType, getRelativeTimeString } from "../utils";
import { TTAND_S3_PREFIX } from "@/types/constants/common";
import AddCommentForm from "./AddCommentForm";
import AddNoteModal from "./AddNoteModal";
import {
  ArchiveOutlined,
  ChecklistOutlined,
  EventOutlined,
  PostAddOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { useState } from "react";
import { NoteObj } from "@/types/interfaces/notes";
import Loader from "@/components/Loader/Loader";
import ErrorFallback from "@/components/ErrorFallback/ErrorFallback";
import { toast } from "sonner";
import SectionCard from "./SectionCard";

type NotesSectionProps = {
  email: string;
};

const getAvatarSrc = (email?: string) => {
  const handle = email?.split("@")?.[0];
  return handle ? `${TTAND_S3_PREFIX}/uploads/users/avatars/${handle}.jpg` : undefined;
};

const getNoteStatus = (note: NoteObj): { label: string; color: ColorPaletteProp } => {
  if (!note.active) return { label: "Archived", color: "neutral" };
  if (note.reviewed) return { label: "Resolved", color: "success" };
  return { label: "Open", color: "warning" };
};

const formatReviewDate = (reviewOn: NoteObj["reviewOn"]) => {
  if (!reviewOn) return null;
  const date = new Date(reviewOn);
  return isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

function NotesSection({ email }: NotesSectionProps) {
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { query: notesData, notes: allNotes, openNotes } = useAgentNotes(email);

  const handleArchive = async (note: NoteObj) => {
    try {
      setArchivingId(note._id);

      await updateNotes(note._id, {
        active: false,
      });

      // invalidate agent notes query
      queryClient.invalidateQueries({ queryKey: ["agentNotes", email] });

      toast.success("Note archived successfully");
    } catch (err: any) {
      const res = err.response?.data;

      toast.error(res?.message || "Failed to archive note");
    } finally {
      setArchivingId(null);
    }
  };

  const filteredNotes = allNotes.filter((note) => note.active || showArchived);
  const openCount = openNotes.length;

  return (
    <>
      <SectionCard
        title="Notes"
        icon={<ChecklistOutlined />}
        action={
          <Stack direction="row" alignItems="center" gap={2}>
            {openCount > 0 && (
              <Chip size="sm" variant="soft" color="warning">
                {openCount} open
              </Chip>
            )}
            <Typography
              component="label"
              level="body-sm"
              startDecorator={
                <Switch size="sm" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              }
              sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Show archived
            </Typography>
            <Button size="sm" startDecorator={<PostAddOutlined />} onClick={() => setShowAddNoteModal(true)}>
              Add Note
            </Button>
          </Stack>
        }
      >
        <AddNoteModal open={showAddNoteModal} onClose={() => setShowAddNoteModal(false)} email={email} />

        {notesData.isLoading ? (
          <Loader />
        ) : notesData.error ? (
          <ErrorFallback text="Couldn't load the notes" retry={notesData.refetch} />
        ) : filteredNotes.length ? (
          <Stack gap={2}>
            {filteredNotes.map((note: NoteObj) => {
              const status = getNoteStatus(note);
              const reviewDate = formatReviewDate(note.reviewOn);
              const author = note.fromEmail?.split("@")[0];

              return (
                <Stack direction="row" gap={1.5} key={note._id} sx={{ opacity: note.active ? 1 : 0.65 }}>
                  <Avatar src={getAvatarSrc(note.fromEmail)} size="md" sx={{ flexShrink: 0 }}>
                    {author?.[0]?.toUpperCase()}
                  </Avatar>

                  <Stack
                    gap={1.25}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      p: 2,
                      borderRadius: "lg",
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.level1",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Stack gap={0.75}>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography level="title-md">{getFilteredType(note.type)}</Typography>
                          <Chip size="sm" variant="soft" color={status.color}>
                            {status.label}
                          </Chip>
                          {reviewDate && (
                            <Chip size="sm" variant="outlined" color="neutral" startDecorator={<EventOutlined />}>
                              Review on {reviewDate}
                            </Chip>
                          )}
                        </Stack>
                        <Typography level="body-xs" textColor="text.tertiary">
                          {author ? `@${author} · ` : ""}
                          {getRelativeTimeString(note.createdAt)}
                        </Typography>
                      </Stack>

                      {note.active && note.reviewed && (
                        <Tooltip title="Archive" placement="top" size="sm">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            aria-label="Archive note"
                            disabled={!!archivingId}
                            onClick={() => handleArchive(note)}
                          >
                            {archivingId === note._id ? <CircularProgress size="sm" /> : <ArchiveOutlined />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>

                    <Typography level="body-md" textColor="text.primary" sx={{ whiteSpace: "pre-line" }}>
                      {note.comment}
                    </Typography>

                    {note.active && !note.reviewed && (
                      <Box sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                        <AddCommentForm note={note} email={email} />
                      </Box>
                    )}

                    {note.reviewed && note.reply && note.replyBy && (
                      <Stack
                        direction="row"
                        gap={1.25}
                        sx={{
                          p: 1.5,
                          borderRadius: "md",
                          backgroundColor: "background.surface",
                          borderLeft: "3px solid",
                          borderLeftColor: "success.400",
                        }}
                      >
                        <Avatar src={getAvatarSrc(note.replyBy)} size="sm" sx={{ flexShrink: 0 }} />
                        <Stack gap={0.5} sx={{ minWidth: 0 }}>
                          <Typography
                            level="body-xs"
                            textColor="text.secondary"
                            startDecorator={<TaskAltOutlined sx={{ color: "success.500" }} fontSize="sm" />}
                          >
                            Resolved by
                            <Typography component="span" fontWeight="lg" textColor="primary.600" ml={0.5}>
                              @{note.replyBy.split("@")[0]}
                            </Typography>
                          </Typography>
                          <Typography level="body-sm" textColor="text.primary" sx={{ whiteSpace: "pre-line" }}>
                            {note.reply}
                          </Typography>
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        ) : (
          <Stack alignItems="center" gap={1} sx={{ py: 6, borderRadius: "md", backgroundColor: "background.level1" }}>
            <ChecklistOutlined sx={{ color: "text.tertiary", fontSize: "2rem" }} />
            <Typography level="title-sm">{allNotes.length ? "All notes are archived" : "No notes yet"}</Typography>
            {!allNotes.length && (
              <Typography level="body-sm" textColor="text.tertiary">
                Notes added for this agent will show up here.
              </Typography>
            )}
          </Stack>
        )}
      </SectionCard>
    </>
  );
}

export default NotesSection;
